import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaWarehouse,
  FaStore,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaHistory,
  FaChevronDown,
  FaChevronUp,
  FaCheck,
  FaTimes,
  FaSync,
  FaBoxOpen,
  FaClipboardCheck,
  FaClipboardList,
  FaEdit,
  FaEye
} from "react-icons/fa";
import {
  getPendingApprovals,
  getRequestDetails,
  submitBranchReview,
  approveFinalDistribution,
  rejectDistribution,
  getDistributionHistory,
  getApprovalCounts
} from "../services/ClientService";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";

const InventoryApprovals = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pending"); // pending, history
  const [isLoading, setIsLoading] = useState(false);
  const [approvals, setApprovals] = useState([]);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({
    pending_branch_review: 0,
    pending_warehouse_approval: 0,
    pending_warehouse_review: 0,
    total: 0
  });
  const [isWarehouse, setIsWarehouse] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [itemInputs, setItemInputs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showFinalApproveConfirm, setShowFinalApproveConfirm] = useState(false);
  const [finalApproveMessage, setFinalApproveMessage] = useState("");

  // Fetch pending approvals
  const fetchApprovals = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getPendingApprovals({ viewType: "all" });
      if (response.code === 200 && response.success) {
        setApprovals(response.data || []);
        setCounts(response.counts || { total: 0 });
        setIsWarehouse(response.isWarehouse || false);
      }
    } catch (error) {
      console.error("Error fetching approvals:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch distribution history
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getDistributionHistory({ page: 1, limit: 50 });
      if (response.code === 200 && response.success) {
        setHistory(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === "pending") {
      fetchApprovals();
    } else if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab, fetchApprovals, fetchHistory]);

  // Refresh counts
  const refreshCounts = async () => {
    try {
      const response = await getApprovalCounts({});
      if (response.code === 200 && response.success) {
        setCounts(response.data || { total: 0 });
      }
    } catch (error) {
      console.error("Error refreshing counts:", error);
    }
  };

  // Helper function to group common inventory items by productId
  // For UI display only - backend still receives individual items
  // Common inventory: Multiple options share the SAME quantity pool, so we show the quantity once (not summed)
  const groupItemsForDisplay = (items) => {
    if (!items || items.length === 0) return [];

    const grouped = [];
    const commonInventoryGroups = {};

    items.forEach(item => {
      if (item.commonInventory) {
        // Group common inventory items by productId
        // For common inventory, all options share the same quantity - don't sum them
        if (!commonInventoryGroups[item.productId]) {
          commonInventoryGroups[item.productId] = {
            ...item,
            isGrouped: true,
            groupedItems: [item], // Keep reference to all items in group
            // Use requestedQuantity from first item (they all share the same pool)
            totalRequestedQuantity: item.requestedQuantity,
            totalDefectiveQuantity: item.defectiveQuantity || 0,
            displayName: item.productName, // Show only product name
            optionLabel: `Common Inventory` // Simplified label - no options count
          };
        } else {
          // Add to existing group but DON'T sum quantities (common inventory shares same pool)
          commonInventoryGroups[item.productId].groupedItems.push(item);
          // Only sum defective if reported
          if (item.defectiveQuantity > 0) {
            commonInventoryGroups[item.productId].totalDefectiveQuantity += (item.defectiveQuantity || 0);
          }
        }
      } else {
        // Non-common inventory items display individually
        grouped.push({
          ...item,
          isGrouped: false,
          displayName: item.productName,
          totalRequestedQuantity: item.requestedQuantity
        });
      }
    });

    // Add grouped common inventory items
    Object.values(commonInventoryGroups).forEach(group => {
      grouped.push(group);
    });

    return grouped;
  };

  // Open detail modal
  const handleViewDetails = async (request) => {
    setIsLoading(true);
    try {
      const response = await getRequestDetails({ requestId: request._id });
      if (response.code === 200 && response.success) {
        setSelectedRequest(response.data);
        // Initialize item inputs with current values
        const inputs = {};
        response.data.items?.forEach((item) => {
          const key = `${item.productId}_${item.productOptionId}`;
          inputs[key] = {
            defectiveQuantity: item.defectiveQuantity || 0,
            currentQuantity: item.currentQuantity || item.requestedQuantity,
            branchNotes: item.branchNotes || "",
            warehouseNotes: item.warehouseNotes || ""
          };
        });
        setItemInputs(inputs);
        setNotes(response.data.notes || "");
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error("Error fetching request details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle item expansion
  const toggleItemExpansion = (key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Update item input - handles both individual and grouped items
  const handleItemInputChange = (key, field, value, groupedItems = null) => {
    if (groupedItems && groupedItems.length > 0) {
      // For common inventory grouped items, update all items in the group
      // Apply the SAME defective quantity to ALL options (not divided)
      setItemInputs((prev) => {
        const newInputs = { ...prev };
        const defectiveValue = parseFloat(value) || 0;

        groupedItems.forEach(item => {
          const itemKey = `${item.productId}_${item.productOptionId}`;
          newInputs[itemKey] = {
            ...newInputs[itemKey],
            // Apply FULL defective quantity to each option (not divided)
            [field]: field === 'defectiveQuantity' ? defectiveValue : value,
            branchNotes: field === 'branchNotes' ? value : (newInputs[itemKey]?.branchNotes || '')
          };
        });

        // Also update the group key for display purposes
        newInputs[key] = {
          ...newInputs[key],
          [field]: value
        };

        return newInputs;
      });
    } else {
      // Individual item update
      setItemInputs((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          [field]: value
        }
      }));
    }
  };

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch (status) {
      case "pending_branch_review":
        return {
          color: "bg-yellow-100 text-yellow-800",
          icon: <FaClock className="text-yellow-600" />,
          label: "Awaiting Branch Review"
        };
      case "pending_warehouse_approval":
        return {
          color: "bg-blue-100 text-blue-800",
          icon: <FaClipboardCheck className="text-blue-600" />,
          label: "Awaiting Warehouse Approval"
        };
      case "pending_warehouse_review":
        return {
          color: "bg-orange-100 text-orange-800",
          icon: <FaWarehouse className="text-orange-600" />,
          label: "Awaiting Warehouse Review"
        };
      case "completed":
        return {
          color: "bg-green-100 text-green-800",
          icon: <FaCheckCircle className="text-green-600" />,
          label: "Completed"
        };
      case "rejected":
        return {
          color: "bg-red-100 text-red-800",
          icon: <FaTimesCircle className="text-red-600" />,
          label: "Rejected"
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          icon: <FaClock className="text-gray-600" />,
          label: status
        };
    }
  };

  // Submit branch review
  const handleSubmitBranchReview = async () => {
    if (!selectedRequest) return;

    setIsSubmitting(true);
    try {
      const items = selectedRequest.items.map((item) => {
        const key = `${item.productId}_${item.productOptionId}`;
        const input = itemInputs[key] || {};
        return {
          productId: item.productId,
          productOptionId: item.productOptionId,
          defectiveQuantity: parseFloat(input.defectiveQuantity) || 0,
          currentQuantity:
            parseFloat(input.currentQuantity) ||
            item.requestedQuantity - (parseFloat(input.defectiveQuantity) || 0),
          branchNotes: input.branchNotes || ""
        };
      });

      const response = await submitBranchReview({
        requestId: selectedRequest._id,
        items,
        notes
      });

      if (response.code === 200 && response.success) {
        toast.success(response.message);
        setShowDetailModal(false);
        fetchApprovals();
        refreshCounts();
      } else {
        toast.error(response.message || "Failed to submit review");
      }
    } catch (error) {
      console.error("Error submitting branch review:", error);
      toast.error("An error occurred while submitting review");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Final approval - Warehouse/Master user approves after branch review
  const handleFinalApproval = () => {
    if (!selectedRequest) return;

    const isBranchRequest = selectedRequest?.requestType === 'branch_request';
    const message = isBranchRequest
      ? `Are you sure you want to approve this stock request and transfer the items?`
      : `Are you sure you want to approve this distribution?`;

    setFinalApproveMessage(message);
    setShowFinalApproveConfirm(true);
  };

  const handleFinalApprovalConfirmed = async () => {
    setShowFinalApproveConfirm(false);
    if (!selectedRequest) return;

    const isBranchRequest = selectedRequest?.requestType === 'branch_request';

    setIsSubmitting(true);
    try {
      const response = await approveFinalDistribution({
        requestId: selectedRequest._id
      });

      if (response.code === 200 && response.success) {
        const successLabel = isBranchRequest ? 'Stock request approved' : 'Distribution approved';
        toast.success(`${successLabel}! ${response.data.totalItems} items transferred successfully to ${response.data.targetBranchName || 'branch'}.`);
        setShowDetailModal(false);
        fetchApprovals();
        refreshCounts();
      } else {
        toast.error(response.message || "Failed to approve distribution");
      }
    } catch (error) {
      console.error("Error approving distribution:", error);
      toast.error("An error occurred while approving distribution");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reject distribution
  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      toast.warning("Please provide a reason for rejection");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await rejectDistribution({
        requestId: selectedRequest._id,
        reason: rejectReason
      });

      if (response.code === 200 && response.success) {
        toast.success("Distribution request rejected");
        setShowRejectModal(false);
        setShowDetailModal(false);
        setRejectReason("");
        fetchApprovals();
        refreshCounts();
      } else {
        toast.error(response.message || "Failed to reject distribution");
      }
    } catch (error) {
      console.error("Error rejecting distribution:", error);
      toast.error("An error occurred while rejecting distribution");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Determine what actions the user can take
  const getAvailableActions = (request) => {
    if (!request) return [];

    const actions = [];
    const status = request.status;

    // Check if user is warehouse or target branch based on context
    // Handles both warehouse distributions and branch stock requests

    if (status === "pending_branch_review") {
      // Branch can review and submit defective items (warehouse distribution flow)
      actions.push({ type: "branch_review", label: "Submit Review" });
    } else if (status === "pending_warehouse_approval") {
      // Warehouse/Master can directly approve after branch review (warehouse distribution flow)
      actions.push({ type: "final_approve", label: "Approve Distribution" });
    } else if (status === "pending_warehouse_review") {
      // Warehouse/Master can approve or reject branch stock request
      if (isWarehouse) {
        actions.push({ type: "final_approve", label: "Approve & Transfer Stock" });
      }
    }

    // Reject is always available for pending requests
    if (status.startsWith("pending_")) {
      actions.push({ type: "reject", label: "Reject" });
    }

    return actions;
  };

  // Render item row for GROUPED common inventory items (simplified view)
  const renderGroupedItemRow = (groupedItem, index, isEditable) => {
    const key = `grouped_${groupedItem.productId}`;
    const isExpanded = expandedItems[key];
    const input = itemInputs[key] || {};
    const status = selectedRequest?.status;
    const isBranchReview = status === "pending_branch_review";
    const isWarehouseApproval = status === "pending_warehouse_approval";

    // For common inventory, all options share the SAME quantity pool
    // So we use the first item's requestedQuantity (not sum of all)
    const totalRequestedQty = groupedItem.groupedItems[0]?.requestedQuantity || 0;
    // Defective is the SAME for all options (not divided), so use first item's value
    const totalDefectiveQty = groupedItem.groupedItems[0]?.defectiveQuantity || 0;
    const metric = groupedItem.selectedMetric || "Kg";

    return (
      <div
        key={key}
        className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-purple-50"
      >
        {/* Item Header - Simplified: Only Product Name */}
        <div
          className="flex items-center justify-between p-4 bg-purple-100 cursor-pointer hover:bg-purple-200 transition-colors"
          onClick={() => toggleItemExpansion(key)}
        >
          <div className="flex items-center gap-3">
            <FaBoxOpen className="text-purple-600" />
            <div>
              <h4 className="font-semibold text-gray-800">{groupedItem.productName}</h4>
              <p className="text-sm text-purple-600">
                Common Inventory
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Requested</p>
              <p className="font-bold text-gray-800">
                {totalRequestedQty} {metric}
              </p>
            </div>
            {totalDefectiveQty > 0 && (
              <div className="text-right">
                <p className="text-sm text-red-500">Defective</p>
                <p className="font-bold text-red-600">-{totalDefectiveQty.toFixed(2)}</p>
              </div>
            )}
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>

        {/* Expanded Content - Simplified */}
        {isExpanded && (
          <div className="p-4 border-t border-purple-200 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Requested</p>
                <p className="font-semibold">
                  {totalRequestedQty} {metric}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Type</p>
                <p className="font-semibold text-purple-600">
                  Common Inventory
                </p>
              </div>
            </div>

            {/* Editable Fields for Branch Review - Single Input */}
            {isEditable && isBranchReview && (
              <div className="border-t border-purple-200 pt-4 space-y-3">
                <h5 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FaEdit className="text-purple-600" />
                  Enter Defective Weight
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Defective Weight ({metric})
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={totalRequestedQty}
                      step="0.01"
                      value={input.defectiveQuantity || ""}
                      onChange={(e) =>
                        handleItemInputChange(
                          key,
                          "defectiveQuantity",
                          e.target.value,
                          groupedItem.groupedItems // Pass grouped items
                        )
                      }
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0"
                    />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <input
                      type="text"
                      value={input.branchNotes || ""}
                      onChange={(e) =>
                        handleItemInputChange(
                          key,
                          "branchNotes",
                          e.target.value,
                          groupedItem.groupedItems
                        )
                      }
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Reason for defective items..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Read-only view for warehouse approval */}
            {status === "pending_warehouse_approval" && (
              <div className="border-t border-purple-200 pt-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Quantity to Transfer:</strong>{" "}
                    {(totalRequestedQty - totalDefectiveQty).toFixed(2)} {metric}
                  </p>
                  {totalDefectiveQty > 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      <strong>Defective reported by branch:</strong> {totalDefectiveQty.toFixed(2)} {metric}
                    </p>
                  )}
                  {groupedItem.groupedItems[0]?.branchNotes && (
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Branch Notes:</strong> {groupedItem.groupedItems[0].branchNotes}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Read-only view for branch stock request pending warehouse review */}
            {status === "pending_warehouse_review" && (
              <div className="border-t border-purple-200 pt-4">
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    <strong>Requested Quantity:</strong>{" "}
                    {totalRequestedQty} {metric}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Branch is requesting this stock from warehouse
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render item row in detail modal (for non-grouped items)
  const renderItemRow = (item, index, isEditable) => {
    const key = `${item.productId}_${item.productOptionId}`;
    const isExpanded = expandedItems[key];
    const input = itemInputs[key] || {};
    const status = selectedRequest?.status;
    const isBranchReview = status === "pending_branch_review";
    const isWarehouseApproval = status === "pending_warehouse_approval";

    return (
      <div
        key={key}
        className="border border-gray-200 rounded-lg mb-3 overflow-hidden"
      >
        {/* Item Header */}
        <div
          className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => toggleItemExpansion(key)}
        >
          <div className="flex items-center gap-3">
            <FaBoxOpen className="text-purple-600" />
            <div>
              <h4 className="font-semibold text-gray-800">{item.productName}</h4>
              <p className="text-sm text-gray-500">{item.optionLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Requested</p>
              <p className="font-bold text-gray-800">
                {item.requestedQuantity} {item.selectedMetric || "units"}
              </p>
            </div>
            {item.defectiveQuantity > 0 && (
              <div className="text-right">
                <p className="text-sm text-red-500">Defective</p>
                <p className="font-bold text-red-600">-{item.defectiveQuantity}</p>
              </div>
            )}
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 border-t border-gray-200 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Requested Qty</p>
                <p className="font-semibold">
                  {item.requestedQuantity} {item.selectedMetric || "units"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Warehouse Available</p>
                <p className="font-semibold text-green-600">
                  {item.warehouseAvailable}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Current Qty</p>
                <p className="font-semibold text-blue-600">
                  {input.currentQuantity || item.currentQuantity}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Common Inventory</p>
                <p className="font-semibold">
                  {item.commonInventory ? "Yes" : "No"}
                </p>
              </div>
            </div>

            {/* Editable Fields for Branch Review */}
            {isEditable && isBranchReview && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <h5 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FaEdit className="text-purple-600" />
                  {item.commonInventory
                    ? "Enter Received Weight"
                    : "Enter Defective Items Count"}
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.commonInventory
                        ? `Defective Weight (${item.selectedMetric || "units"})`
                        : "Defective Quantity"}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={item.requestedQuantity}
                      step={item.commonInventory ? "0.01" : "1"}
                      value={input.defectiveQuantity || ""}
                      onChange={(e) =>
                        handleItemInputChange(
                          key,
                          "defectiveQuantity",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <input
                      type="text"
                      value={input.branchNotes || ""}
                      onChange={(e) =>
                        handleItemInputChange(key, "branchNotes", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Reason for defective items..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Editable Fields for Warehouse Approval - Review defective items reported by branch */}
            {isEditable && isWarehouseApproval && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <h5 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FaWarehouse className="text-indigo-600" />
                  Branch Reported Defective: {item.defectiveQuantity || 0}
                </h5>

                {item.branchNotes && (
                  <p className="text-sm text-gray-600 bg-yellow-50 p-2 rounded">
                    <strong>Branch Notes:</strong> {item.branchNotes}
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adjusted Quantity to Transfer
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={item.requestedQuantity}
                      step={item.commonInventory ? "0.01" : "1"}
                      value={input.currentQuantity || ""}
                      onChange={(e) =>
                        handleItemInputChange(
                          key,
                          "currentQuantity",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder={String(
                        item.requestedQuantity - (item.defectiveQuantity || 0)
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Warehouse Notes
                    </label>
                    <input
                      type="text"
                      value={input.warehouseNotes || ""}
                      onChange={(e) =>
                        handleItemInputChange(
                          key,
                          "warehouseNotes",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Your response..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Read-only view for warehouse approval (simplified flow) */}
            {status === "pending_warehouse_approval" && (
              <div className="border-t border-gray-200 pt-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Quantity to Transfer:</strong>{" "}
                    {item.currentQuantity - (item.defectiveQuantity || 0)}{" "}
                    {item.selectedMetric || "units"}
                  </p>
                  {item.defectiveQuantity > 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      <strong>Defective reported by branch:</strong> {item.defectiveQuantity} {item.selectedMetric || "units"}
                    </p>
                  )}
                  {item.branchNotes && (
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Branch Notes:</strong> {item.branchNotes}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Read-only view for branch stock request pending warehouse review */}
            {status === "pending_warehouse_review" && (
              <div className="border-t border-gray-200 pt-4">
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    <strong>Requested Quantity:</strong>{" "}
                    {item.requestedQuantity} {item.selectedMetric || "units"}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Branch is requesting this stock from warehouse
                  </p>
                </div>
              </div>
            )}


          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/inventory")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaArrowLeft className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Inventory Approvals
                </h1>
                <p className="text-sm text-gray-500">
                  Manage distribution requests and approvals
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                fetchApprovals();
                refreshCounts();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <FaSync className={isLoading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-gray-500">Awaiting Branch Review</p>
            <p className="text-2xl font-bold text-yellow-600">
              {counts.pending_branch_review || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-500">Awaiting Warehouse Approval</p>
            <p className="text-2xl font-bold text-blue-600">
              {counts.pending_warehouse_approval || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-500">
            <p className="text-sm text-gray-500">Branch Stock Requests</p>
            <p className="text-2xl font-bold text-orange-600">
              {counts.pending_warehouse_review || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
            <p className="text-sm text-gray-500">Total Pending</p>
            <p className="text-2xl font-bold text-purple-600">
              {counts.total || 0}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("pending")}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === "pending"
                ? "bg-purple-600 text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
          >
            <FaClipboardList />
            Pending Requests
            {counts.total > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {counts.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === "history"
                ? "bg-purple-600 text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
          >
            <FaHistory />
            History
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : activeTab === "pending" ? (
          <div className="space-y-4">
            {approvals.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <FaClipboardCheck className="mx-auto text-6xl text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600">
                  No Pending Requests
                </h3>
                <p className="text-gray-400 mt-2">
                  All distribution requests have been processed
                </p>
              </div>
            ) : (
              approvals.map((request) => {
                const statusInfo = getStatusInfo(request.status);
                return (
                  <div
                    key={request._id}
                    className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-purple-100 rounded-lg">
                          {request.requestType === 'branch_request' 
                            ? <FaStore className="text-emerald-600 text-xl" />
                            : <FaWarehouse className="text-purple-600 text-xl" />
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-gray-800">
                              {request.requestId}
                            </h3>
                            {request.requestType === 'branch_request' && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Stock Request
                              </span>
                            )}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.color}`}
                            >
                              {statusInfo.icon}
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            {request.requestType === 'branch_request' ? (
                              <>
                                <span className="flex items-center gap-1">
                                  <FaStore className="text-gray-400" />
                                  Requested by: {request.targetBranchName || "Branch"}
                                </span>
                                <span className="flex items-center gap-1">
                                  <FaWarehouse className="text-gray-400" />
                                  From: {request.warehouseName || "Warehouse"}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="flex items-center gap-1">
                                  <FaWarehouse className="text-gray-400" />
                                  From: {request.warehouseName || "Warehouse"}
                                </span>
                                <span className="flex items-center gap-1">
                                  <FaStore className="text-gray-400" />
                                  To: {request.targetBranchName || "Branch"}
                                </span>
                              </>
                            )}
                            <span className="flex items-center gap-1">
                              <FaBoxOpen className="text-gray-400" />
                              {request.totalItems} items
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Created: {formatDate(request.createdAt)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewDetails(request)}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
                      >
                        <FaEye />
                        View & Take Action
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {history.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <FaHistory className="mx-auto text-6xl text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600">
                  No History Yet
                </h3>
                <p className="text-gray-400 mt-2">
                  Completed and rejected requests will appear here
                </p>
              </div>
            ) : (
              history.map((request) => {
                const statusInfo = getStatusInfo(request.status);
                return (
                  <div
                    key={request._id}
                    className="bg-white rounded-xl shadow-sm p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div
                          className={`p-3 rounded-lg ${request.status === "completed"
                              ? "bg-green-100"
                              : "bg-red-100"
                            }`}
                        >
                          {request.status === "completed" ? (
                            <FaCheckCircle className="text-green-600 text-xl" />
                          ) : (
                            <FaTimesCircle className="text-red-600 text-xl" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-gray-800">
                              {request.requestId}
                            </h3>
                            {request.requestType === 'branch_request' && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Stock Request
                              </span>
                            )}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}
                            >
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            <span>From: {request.warehouseName}</span>
                            <span>{request.requestType === 'branch_request' ? 'Requested by' : 'To'}: {request.targetBranchName}</span>
                            <span>{request.totalItems} items</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {request.status === "completed"
                              ? `Completed: ${formatDate(request.completedAt)}`
                              : `Rejected: ${formatDate(request.rejectedAt)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className={`${selectedRequest.requestType === 'branch_request' ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-gradient-to-r from-purple-600 to-indigo-600'} text-white p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-4">
                    {selectedRequest.requestId}
                    {selectedRequest.requestType === 'branch_request' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                        Stock Request
                      </span>
                    )}
                    <span className="flex items-center gap-2">
                      {getStatusInfo(selectedRequest.status).icon}
                      <span className="font-medium">
                        {getStatusInfo(selectedRequest.status).label}
                      </span>
                    </span>
                  </h2>
                  <p className={`${selectedRequest.requestType === 'branch_request' ? 'text-emerald-200' : 'text-purple-200'} text-sm mt-1`}>
                    {selectedRequest.requestType === 'branch_request' 
                      ? `${selectedRequest.targetBranchName} → Requested from ${selectedRequest.warehouseName}`
                      : `${selectedRequest.warehouseName} → ${selectedRequest.targetBranchName}`
                    }
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Items - Use grouped display for common inventory */}
              {(() => {
                const groupedItems = groupItemsForDisplay(selectedRequest.items);
                const displayCount = groupedItems.length;
                return (
                  <>
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FaBoxOpen className="text-purple-600" />
                      Items ({displayCount})
                    </h3>
                    <div className="mb-6">
                      {groupedItems.map((item, index) =>
                        item.isGrouped
                          ? renderGroupedItemRow(
                            item,
                            index,
                            selectedRequest.status.startsWith("pending_")
                          )
                          : renderItemRow(
                            item,
                            index,
                            selectedRequest.status.startsWith("pending_")
                          )
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Notes */}
              {selectedRequest.status.startsWith("pending_") && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    General Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Add any notes or comments..."
                  />
                </div>
              )}

              {/* History */}
              {selectedRequest.history?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaHistory className="text-purple-600" />
                    Activity History
                  </h3>
                  <div className="space-y-3">
                    {selectedRequest.history.map((entry, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 text-sm"
                      >
                        <div
                          className={`w-2 h-2 rounded-full mt-2 ${entry.action === "created"
                              ? "bg-blue-500"
                              : entry.action === "approved"
                                ? "bg-green-500"
                                : entry.action === "rejected"
                                  ? "bg-red-500"
                                  : "bg-gray-400"
                            }`}
                        />
                        <div>
                          <p className="text-gray-800">{entry.details}</p>
                          <p className="text-gray-400 text-xs">
                            {entry.by} • {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex flex-wrap gap-3 justify-end">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Close
                </button>

                {/* Branch user: Submit review with defective items (only for warehouse distribution flow) */}
                {selectedRequest.status === "pending_branch_review" && !isWarehouse && (
                  <button
                    onClick={handleSubmitBranchReview}
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                    ) : (
                      <FaCheck />
                    )}
                    Submit Review
                  </button>
                )}

                {/* Warehouse/Master: Approve or Reject distribution (after branch review) */}
                {selectedRequest.status === "pending_warehouse_approval" && isWarehouse && (
                  <>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                    >
                      <FaTimes />
                      Reject
                    </button>
                    <button
                      onClick={handleFinalApproval}
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                      ) : (
                        <FaCheckCircle />
                      )}
                      Approve Distribution
                    </button>
                  </>
                )}

                {/* Warehouse/Master: Approve or Reject branch stock request */}
                {selectedRequest.status === "pending_warehouse_review" && isWarehouse && (
                  <>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                    >
                      <FaTimes />
                      Reject Request
                    </button>
                    <button
                      onClick={handleFinalApproval}
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                      ) : (
                        <FaCheckCircle />
                      )}
                      Approve & Transfer Stock
                    </button>
                  </>
                )}

                {/* Branch can cancel their own stock request */}
                {selectedRequest.status === "pending_warehouse_review" && !isWarehouse && (
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                  >
                    <FaTimes />
                    Cancel Request
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Reject Distribution Request
            </h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this request:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              placeholder="Enter reason for rejection..."
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={isSubmitting || !rejectReason.trim()}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        visible={showFinalApproveConfirm}
        message={finalApproveMessage}
        onConfirm={handleFinalApprovalConfirmed}
        onCancel={() => setShowFinalApproveConfirm(false)}
        cancelButtonText="Cancel"
        confirmButtonText="Approve"
      />
    </div>
  );
};

export default InventoryApprovals;
